import { safePercent } from "../../utils/format-number";
import StatNumber from "./stat-number";

type Props = { data: Record<string, any> | null };

const n = (v: unknown) => Number(v ?? 0) || 0;

/**
 * Outstanding balance split, plus the three debt figures.
 *
 * The split is a single part-to-whole meter rather than two numbers side by side —
 * "how much sits with collectors" is a proportion, and a meter answers it without the
 * reader doing arithmetic.
 */
export default function DebtPanel({ data }: Props) {
  const d = data ?? {};

  const icb = n(d.icbOutstandingBalance);
  const internal = n(d.internalOutstandingBalance);
  const outstanding = icb + internal;

  const figures = [
    { label: "Average debt", value: n(d.avgTotalAmount) },
    { label: "Written off", value: n(d.debtWrittenOff) },
    { label: "SBV1 collections", value: n(d.sbv1Collections) },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="flex items-baseline justify-between gap-3 mb-2">
          <p className="text-[13px] font-medium text-text-secondary">
            Outstanding balance
          </p>
          <p className="text-[16px] font-bold text-text-primary shrink-0">
            <StatNumber value={outstanding} currency />
          </p>
        </div>

        <div className="flex h-1.5 w-full rounded-full bg-overlay-mid overflow-hidden gap-[2px]">
          <div
            className="h-full rounded-full bg-sky-500 transition-all duration-500"
            style={{ width: `${safePercent(icb, outstanding)}%` }}
          />
          <div
            className="h-full rounded-full bg-indigo-500 transition-all duration-500"
            style={{ width: `${safePercent(internal, outstanding)}%` }}
          />
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1">
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />
            <span className="text-[12px] font-medium text-light-gray">
              With collectors
            </span>
            <span className="text-[12px] font-semibold text-text-primary">
              <StatNumber value={icb} currency />
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
            <span className="text-[12px] font-medium text-light-gray">Internal</span>
            <span className="text-[12px] font-semibold text-text-primary">
              <StatNumber value={internal} currency />
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-edge-faint">
        {figures.map((f) => (
          <div key={f.label}>
            <p className="text-[11px] font-medium text-light-gray leading-tight">
              {f.label}
            </p>
            <p className="mt-1 text-[15px] font-bold text-text-primary">
              <StatNumber value={f.value} currency />
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
